"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

import { formatNoteMonthYear } from "@/lib/dates";

type TeaserNote = {
  slug: string;
  title: string;
  publishedAt: string;
};

/**
 * Home page notes teaser. Renders nothing until there is something to show,
 * so the section never appears as an empty block before the first note is
 * published or if the database is unreachable.
 */
export default function LatestNotes() {
  const [notes, setNotes] = useState<TeaserNote[]>([]);

  useEffect(() => {
    const controller = new AbortController();

    fetch("/api/notes/latest", { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((data: { notes?: TeaserNote[] } | null) => {
        if (data?.notes?.length) setNotes(data.notes);
      })
      .catch(() => {
        /* nothing to show */
      });

    return () => controller.abort();
  }, []);

  if (notes.length === 0) return null;

  return (
    <section
      id="notes-section"
      className="relative z-20 bg-[#fafcfd] px-6 py-24 md:px-12 lg:px-20"
    >
      <div>
        <div className="mb-12 flex flex-wrap items-baseline justify-between gap-4">
          <p className="text-sm uppercase tracking-[0.15em] text-[#6b6b6b]">
            Notes
          </p>
          <Link
            href="/notes"
            className="text-[14px] font-medium text-[#111111] transition-opacity duration-200 hover:opacity-60"
          >
            All notes →
          </Link>
        </div>

        <ul>
          {notes.map((note) => (
            <li key={note.slug}>
              <Link
                href={`/notes/${note.slug}`}
                className="group flex flex-wrap items-baseline gap-x-3 py-8 transition-opacity duration-200 hover:opacity-70"
              >
                <h3 className="lowercase font-pp-acma text-[clamp(20px,4vw,26px)] font-semibold leading-[1.2] text-[#111111]">
                  {note.title}
                </h3>
                <span aria-hidden="true" className="text-[#6b6b6b]">
                  -
                </span>
                <time
                  dateTime={note.publishedAt}
                  className="font-pp-neuebit text-[18px] tracking-[0.12em] text-[#6b6b6b]"
                >
                  {formatNoteMonthYear(note.publishedAt)}
                </time>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
